import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { paymentService } from '../../services/paymentService';
import OrderSummary from './components/OrderSummary';
import PaymentMethods from './components/PaymentMethods';
import PaymentForm from './components/PaymentForm';
import { CreditCard, Shield, AlertCircle } from 'lucide-react';

const PaymentCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [orderData, setOrderData] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('payfast');
  const [paymentForm, setPaymentForm] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: '',
    billingAddress: {
      street: '',
      city: '',
      province: '',
      postalCode: ''
    }
  });
  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);

  // Load order data from URL params or localStorage
  useEffect(() => {
    const orderId = searchParams?.get('order');
    const serviceId = searchParams?.get('service');
    const productId = searchParams?.get('product');
    
    if (orderId) {
      loadOrderData(orderId);
    } else if (serviceId || productId) {
      createQuickOrder({ serviceId, productId });
    } else {
      // Check for cart data in localStorage
      const cartData = localStorage.getItem('checkout_cart');
      if (cartData) {
        const parsedCart = JSON.parse(cartData);
        createOrderFromCart(parsedCart);
      } else {
        navigate('/e-commerce-store');
      }
    }
  }, [searchParams, user]);

  const loadOrderData = async (orderId) => {
    setLoading(true);
    const { data, error } = await paymentService?.getOrderById(orderId);
    if (error) {
      setError('Failed to load order details');
    } else {
      setOrderData(data);
    }
    setLoading(false);
  };

  const createQuickOrder = async ({ serviceId, productId }) => {
    if (!user) {
      navigate('/login?redirect=/payment-checkout');
      return;
    }

    setLoading(true);
    try {
      // Create draft order
      const orderData = {
        user_id: user?.id,
        status: 'draft',
        customer_name: user?.userProfile?.full_name || user?.email?.split('@')?.[0] || 'Customer',
        customer_email: user?.email,
        service_address: ''
      };

      const { data: order, error: orderError } = await paymentService?.createOrder(orderData);
      if (orderError) throw new Error(orderError);

      // Add order item
      if (serviceId) {
        const { data: services } = await paymentService?.getServices();
        const service = services?.find(s => s?.id === serviceId);
        if (service) {
          await paymentService?.addOrderItem({
            order_id: order?.id,
            service_id: serviceId,
            quantity: 1,
            unit_price: service?.price,
            total_price: service?.price
          });
        }
      }

      if (productId) {
        const { data: products } = await paymentService?.getProducts();
        const product = products?.find(p => p?.id === productId);
        if (product) {
          await paymentService?.addOrderItem({
            order_id: order?.id,
            product_id: productId,
            quantity: 1,
            unit_price: product?.price,
            total_price: product?.price
          });
        }
      }

      // Reload order with items
      await loadOrderData(order?.id);
    } catch (err) {
      setError('Failed to create order: ' + err?.message);
    }
    setLoading(false);
  };

  const createOrderFromCart = async (cartData) => {
    if (!user) {
      navigate('/login?redirect=/payment-checkout');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        user_id: user?.id,
        status: 'draft',
        customer_name: cartData?.customerName || user?.userProfile?.full_name || user?.email?.split('@')?.[0] || 'Customer',
        customer_email: cartData?.customerEmail || user?.email,
        customer_phone: cartData?.customerPhone || '',
        service_address: cartData?.serviceAddress || ''
      };

      const { data: order, error: orderError } = await paymentService?.createOrder(orderData);
      if (orderError) throw new Error(orderError);

      // Add cart items
      for (const item of cartData?.items || []) {
        await paymentService?.addOrderItem({
          order_id: order?.id,
          service_id: item?.service_id,
          product_id: item?.product_id,
          quantity: item?.quantity,
          unit_price: item?.unit_price,
          total_price: item?.total_price
        });
      }

      await loadOrderData(order?.id);
      localStorage.removeItem('checkout_cart');
    } catch (err) {
      setError('Failed to process cart: ' + err?.message);
    }
    setLoading(false);
  };

  const applyPromoCode = () => {
    // Mock promo code logic
    const validCodes = {
      'CLEAN10': 10,
      'SAVE20': 20,
      'FIRST25': 25
    };
    
    if (validCodes?.[promoCode]) {
      setDiscount(validCodes?.[promoCode]);
      setError('');
    } else {
      setError('Invalid promo code');
      setDiscount(0);
    }
  };

  const handlePayment = async () => {
    if (!orderData || !user) return;

    setLoading(true);
    setError('');

    try {
      // Create payment record
      const paymentData = {
        order_id: orderData?.id,
        user_id: user?.id,
        payment_gateway: selectedPaymentMethod,
        status: 'processing',
        amount: calculateFinalTotal(),
        currency: 'ZAR'
      };

      const { data: payment, error: paymentError } = await paymentService?.createPayment(paymentData);
      if (paymentError) throw new Error(paymentError);

      // Process payment based on selected method
      let paymentResult;
      if (selectedPaymentMethod === 'payfast') {
        paymentResult = await paymentService?.processPayFastPayment({
          amount: calculateFinalTotal(),
          order_id: orderData?.id,
          ...paymentForm
        });
      } else if (selectedPaymentMethod === 'paystack') {
        paymentResult = await paymentService?.processPaystackPayment({
          amount: calculateFinalTotal(),
          order_id: orderData?.id,
          ...paymentForm
        });
      }

      if (paymentResult?.error) {
        throw new Error(paymentResult.error);
      }

      // Update payment status
      await paymentService?.updatePaymentStatus(payment?.id, 'completed', {
        gateway_transaction_id: paymentResult?.data?.transaction_id,
        gateway_reference: paymentResult?.data?.reference,
        gateway_response: paymentResult?.data
      });

      // Update order status
      await paymentService?.updateOrder(orderData?.id, { 
        status: 'confirmed',
        total_amount: calculateFinalTotal()
      });

      // Navigate to success page
      navigate(`/payment-status-tracking?payment=${payment?.id}&status=success`);
    } catch (err) {
      setError('Payment failed: ' + err?.message);
    }
    setLoading(false);
  };

  const calculateSubtotal = () => {
    return orderData?.order_items?.reduce((total, item) => total + item?.total_price, 0) || 0;
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.15; // 15% VAT
  };

  const calculateDiscount = () => {
    return calculateSubtotal() * (discount / 100);
  };

  const calculateFinalTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    const discountAmount = calculateDiscount();
    return subtotal + tax - discountAmount;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Sign in Required</h2>
          <p className="text-gray-600 mb-6">Please sign in to continue with your purchase.</p>
          <button
            onClick={() => navigate('/login?redirect=/payment-checkout')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3]?.map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-2 space-x-20 text-sm text-gray-600">
            <span>Review Order</span>
            <span>Payment</span>
            <span>Confirmation</span>
          </div>
        </div>

        {/* Security Badges */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center">
              <Shield className="h-4 w-4 mr-1 text-green-500" />
              SSL Secured
            </div>
            <div className="flex items-center">
              <CreditCard className="h-4 w-4 mr-1 text-blue-500" />
              PCI Compliant
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              {error}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Panel - Order Summary */}
          <div className="lg:order-2">
            <OrderSummary
              orderData={orderData}
              subtotal={calculateSubtotal()}
              tax={calculateTax()}
              discount={calculateDiscount()}
              total={calculateFinalTotal()}
              promoCode={promoCode}
              onPromoCodeChange={setPromoCode}
              onApplyPromo={applyPromoCode}
              paymentService={paymentService}
            />
          </div>

          {/* Right Panel - Payment */}
          <div className="lg:order-1">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Details</h2>

              <PaymentMethods
                selectedMethod={selectedPaymentMethod}
                onMethodSelect={setSelectedPaymentMethod}
              />

              <PaymentForm
                paymentMethod={selectedPaymentMethod}
                formData={paymentForm}
                onFormChange={setPaymentForm}
              />

              <div className="mt-8">
                <button
                  onClick={handlePayment}
                  disabled={loading || !orderData}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : (
                    <CreditCard className="h-5 w-5 mr-2" />
                  )}
                  Complete Payment - {paymentService?.formatCurrency(calculateFinalTotal())}
                </button>
              </div>

              <div className="mt-4 text-center text-sm text-gray-600">
                <p>Your payment is secured with industry-standard encryption</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckout;